import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  increment,
  limit as fbLimit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from 'shared/services/firebase';
import { callFunction } from 'shared/services/functions';
import { clientEnvironment } from 'shared/platform/environment';
import {
  createDoc,
  getDocOnce,
  getQueryOnce,
  runTransaction,
  subscribeToDoc,
  subscribeToQuery,
} from 'shared/services/firestore';
import { uploadFile } from 'shared/services/storage';
import {
  buildNewMessageDoc,
  buildNewReactionDoc,
  buildReadReceiptDoc,
  canonicalReadReceiptId,
  Channel,
  ChannelMembership,
  ChannelReadReceipt,
  DEFAULT_CHANNEL_NOTIFICATION_PREFERENCE,
  effectiveNotificationPreferenceForChannelName,
  LEGACY_CHANNEL_NOTIFICATION_PREFERENCE,
  mapChannel,
  mapChannelMembership,
  mapChannelReadReceipt,
  mapMsgBoardMessage,
  mapReaction,
  MessageBoardAttachment,
  MsgBoardMessage,
  NotificationPreference,
  Reaction,
  ReadReceiptPreservedFields,
} from 'domains/messaging/model/messageBoard';
import {
  latestReadReceiptsByChannel,
  resolveMessageBoardReadCutoff,
  resolveMessageBoardReadMessageId,
} from 'domains/messaging/application/messageBoardUnread';

/**
 * Message board reads + `markChannelAsRead` write. Ported from aligned's
 * `MessageBoardService` (`lib/services/message_board_service.dart`). Keeps the
 * same collection paths (`channels`, `channel_memberships`, `messages`,
 * `channel_read_receipts` — all top-level), the same query shapes, and the
 * same date encoding (ISO strings, not Firestore Timestamps).
 *
 * The service keeps aligned collection paths. Employee display designations
 * add an optional `isEmployee` flag to channel memberships; legacy memberships
 * default to false and message documents do not need migration.
 */

const CHANNELS = 'channels';
const CHANNEL_MEMBERSHIPS = 'channel_memberships';
const CHANNEL_READ_RECEIPTS = 'channel_read_receipts';
const MESSAGES = 'messages';
const REACTIONS = 'reactions';

const MESSAGES_LIVE_LIMIT = 50; // matches aligned's startListeningToChannel limit
/** Matches aligned's `getChannelMessages` default. Exported so callers can
 *  tell a full page (more history behind it) from a short, final one. */
export const MESSAGES_INITIAL_PAGE = 10;
const MESSAGE_CHANNEL_QUERY_LIMIT = 30; // Firestore `in` disjunction limit

// Aligned suppresses duplicate read writes for the lifetime of its service
// instance, keyed by channel.
const lastMarkedReadMessageIds = new Map<string, string>();

// ---------------------------------------------------------------------------
// Channel + membership + receipt reads
// ---------------------------------------------------------------------------

/** Admin/channel-picker read: every non-deprecated group channel. */
export async function getAvailableChannels(): Promise<Channel[]> {
  return getQueryOnce(
    query(collection(db, CHANNELS), where('deprecatedOn', '==', null), where('type', '==', 'group')),
    mapChannel,
  );
}

/** Non-deprecated memberships for the current user. */
export async function getUserMemberships(userId: string): Promise<ChannelMembership[]> {
  const q = query(
    collection(db, CHANNEL_MEMBERSHIPS),
    where('userId', '==', userId),
    where('deprecatedOn', '==', null),
  );
  return getQueryOnce(q, mapChannelMembership);
}

/** Live non-deprecated memberships for the current user. */
export function subscribeToUserMemberships(
  userId: string,
  onNext: (memberships: ChannelMembership[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, CHANNEL_MEMBERSHIPS),
    where('userId', '==', userId),
    where('deprecatedOn', '==', null),
  );
  return subscribeToQuery(q, mapChannelMembership, onNext, {
    context: `subscribeToUserMemberships(${userId})`,
    onError,
  });
}

/**
 * Channels the user is a member of, matching aligned's
 * `getAvailableChannelsForUser`:
 * 1. Fetch memberships (userId + not deprecated).
 * 2. For each unique channelId, fetch the channel doc.
 * 3. Filter out deprecated channels.
 * (Sorting by recent activity happens in the viewmodel/UI, not here.)
 */
export async function getAvailableChannelsForUser(userId: string): Promise<Channel[]> {
  const memberships = await getUserMemberships(userId);
  const channelIds = Array.from(new Set(memberships.map((m) => m.channelId).filter(Boolean)));
  if (channelIds.length === 0) return [];

  const snaps = await Promise.all(
    channelIds.map((id) => getDocOnce(doc(db, CHANNELS, id), mapChannel)),
  );
  return snaps.filter((c): c is Channel => c !== null && c.deprecatedOn === null);
}

/** Fetch channel docs by id, dropping any that are missing or deprecated. Used
 *  to resolve a campaign's `expertMessageBoardList`/`reviewerMessageBoardList`
 *  channel ids into `Channel`s (matches aligned's `getChannel` loop in
 *  `getChannelsForCampaign`). */
export async function getChannelsByIds(channelIds: string[]): Promise<Channel[]> {
  const uniqueIds = Array.from(new Set(channelIds.filter(Boolean)));
  if (uniqueIds.length === 0) return [];
  const snaps = await Promise.all(uniqueIds.map((id) => getDocOnce(doc(db, CHANNELS, id), mapChannel)));
  return snaps.filter((c): c is Channel => c !== null && c.deprecatedOn === null);
}

/** Non-deprecated memberships for a given channel. */
export async function getChannelMembers(channelId: string): Promise<ChannelMembership[]> {
  const q = query(
    collection(db, CHANNEL_MEMBERSHIPS),
    where('channelId', '==', channelId),
    where('deprecatedOn', '==', null),
  );
  return getQueryOnce(q, mapChannelMembership);
}

/** Stream author designations so existing messages reflect role changes. */
export function subscribeToChannelMembers(
  channelId: string,
  onNext: (members: ChannelMembership[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, CHANNEL_MEMBERSHIPS),
    where('channelId', '==', channelId),
    where('deprecatedOn', '==', null),
  );
  return subscribeToQuery(q, mapChannelMembership, onNext, {
    context: `subscribeToChannelMembers(${channelId})`,
    onError,
  });
}

/**
 * Find a direct-message channel between two users. Ported from aligned's
 * `findDirectMessageChannel`: fetch each user's memberships, intersect the
 * channelIds, then load each candidate channel and pick the first
 * non-deprecated `direct_message` one. Returns `null` if none.
 */
export async function findDirectMessageChannel(
  userId1: string,
  userId2: string,
): Promise<Channel | null> {
  if (!userId1 || !userId2 || userId1 === userId2) return null;
  const [m1, m2] = await Promise.all([getUserMemberships(userId1), getUserMemberships(userId2)]);
  if (m1.length === 0 || m2.length === 0) return null;
  const set1 = new Set(m1.map((m) => m.channelId));
  const commonIds = Array.from(new Set(m2.map((m) => m.channelId).filter((id) => set1.has(id))));
  if (commonIds.length === 0) return null;
  const snaps = await Promise.all(
    commonIds.map((id) => getDocOnce(doc(db, CHANNELS, id), mapChannel)),
  );
  return (
    snaps.find(
      (c): c is Channel => c !== null && c.deprecatedOn === null && c.type === 'direct_message',
    ) ?? null
  );
}

/** Non-deprecated read receipts for the current user. Callers deduplicate by channel. */
export async function getUserReadReceipts(userId: string): Promise<ChannelReadReceipt[]> {
  const q = query(
    collection(db, CHANNEL_READ_RECEIPTS),
    where('userId', '==', userId),
    where('deprecatedOn', '==', null),
  );
  return getQueryOnce(q, mapChannelReadReceipt);
}

/**
 * Live read-receipt stream for the current user. Aligned keeps this listener
 * open after the initial load so a read made by another client immediately
 * clears the local board badge instead of waiting for a page reload.
 */
export function subscribeToUserReadReceipts(
  userId: string,
  onNext: (receipts: ChannelReadReceipt[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, CHANNEL_READ_RECEIPTS),
    where('userId', '==', userId),
    where('deprecatedOn', '==', null),
  );
  return subscribeToQuery(q, mapChannelReadReceipt, onNext, {
    context: `subscribeToUserReadReceipts(${userId})`,
    onError,
  });
}

// ---------------------------------------------------------------------------
// Message reads (one-shot + real-time)
// ---------------------------------------------------------------------------

/**
 * Initial page of messages for a channel — newest-first, non-deprecated,
 * capped at `pageSize` (default matches aligned's 10). Callers order by
 * `createdAt DESC`; the ported viewmodel reverses in the render.
 */
export function getChannelMessages(
  channelId: string,
  pageSize: number = MESSAGES_INITIAL_PAGE,
  beforeCreatedAt?: string | null,
): Promise<MsgBoardMessage[]> {
  const constraints = [
    where('channelId', '==', channelId),
    where('deprecatedOn', '==', null),
    ...(beforeCreatedAt ? [where('createdAt', '<', beforeCreatedAt)] : []),
    orderBy('createdAt', 'desc'),
    fbLimit(pageSize),
  ];
  const q = query(
    collection(db, MESSAGES),
    ...constraints,
  );
  return getQueryOnce(q, mapMsgBoardMessage);
}

/** Fetch the next older page using aligned's `createdAt < beforeDate` rule. */
export function getOlderChannelMessages(
  channelId: string,
  beforeCreatedAt: string,
  pageSize = MESSAGES_INITIAL_PAGE,
): Promise<MsgBoardMessage[]> {
  return getChannelMessages(channelId, pageSize, beforeCreatedAt);
}

export interface RecentChannelMessagesBatchCursor {
  channelIds: string[];
  afterCreatedAt: string | null;
  afterMessageId: string | null;
  exhausted: boolean;
}

export interface RecentChannelMessagesCursor {
  batches: RecentChannelMessagesBatchCursor[];
}

export interface RecentChannelMessagesPage {
  messages: MsgBoardMessage[];
  cursor: RecentChannelMessagesCursor;
  hasMore: boolean;
}

function channelIdBatches(channelIds: string[]): RecentChannelMessagesBatchCursor[] {
  const uniqueIds = Array.from(new Set(channelIds.filter(Boolean)));
  const batches: RecentChannelMessagesBatchCursor[] = [];
  for (let index = 0; index < uniqueIds.length; index += MESSAGE_CHANNEL_QUERY_LIMIT) {
    batches.push({
      channelIds: uniqueIds.slice(index, index + MESSAGE_CHANNEL_QUERY_LIMIT),
      afterCreatedAt: null,
      afterMessageId: null,
      exhausted: false,
    });
  }
  return batches;
}

/**
 * Scan recent messages across channels the user already has access to. Channel
 * ids are chunked to Firestore's `in` limit and each chunk keeps an independent
 * document cursor, allowing the Threads feed to merge activity across any
 * number of joined boards without querying unrelated messages.
 */
export async function getNextRecentMessagesForChannels(
  channelIds: string[],
  cursor: RecentChannelMessagesCursor | null = null,
  pageSize = 100,
): Promise<RecentChannelMessagesPage> {
  const size = Math.max(1, pageSize);
  const batches = cursor?.batches.length ? cursor.batches : channelIdBatches(channelIds);
  if (batches.length === 0) {
    return { messages: [], cursor: { batches: [] }, hasMore: false };
  }

  const results = await Promise.all(
    batches.map(async (batch) => {
      if (batch.exhausted) return { messages: [] as MsgBoardMessage[], cursor: batch };
      const snapshot = await getDocs(
        query(
          collection(db, MESSAGES),
          where('channelId', 'in', batch.channelIds),
          where('deprecatedOn', '==', null),
          orderBy('createdAt', 'desc'),
          orderBy(documentId(), 'desc'),
          ...(batch.afterCreatedAt && batch.afterMessageId
            ? [startAfter(batch.afterCreatedAt, batch.afterMessageId)]
            : []),
          fbLimit(size),
        ),
      );
      const messages = snapshot.docs.map((message) =>
        mapMsgBoardMessage(message.id, message.data()),
      );
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      const lastMessage = messages[messages.length - 1];
      const canContinue = snapshot.size === size && !!lastDoc && !!lastMessage?.createdAt;
      return {
        messages,
        cursor: {
          channelIds: batch.channelIds,
          afterCreatedAt: canContinue ? lastMessage.createdAt : batch.afterCreatedAt,
          afterMessageId: canContinue ? lastDoc.id : batch.afterMessageId,
          exhausted: !canContinue,
        },
      };
    }),
  );

  const uniqueMessages = new Map<string, MsgBoardMessage>();
  results.forEach((result) => {
    result.messages.forEach((message) => uniqueMessages.set(message.id, message));
  });
  const messages = Array.from(uniqueMessages.values()).sort((first, second) => {
    const byDate = (second.createdAt ?? '').localeCompare(first.createdAt ?? '');
    return byDate || second.id.localeCompare(first.id);
  });
  const nextCursor = { batches: results.map((result) => result.cursor) };
  return {
    messages,
    cursor: nextCursor,
    hasMore: nextCursor.batches.some((batch) => !batch.exhausted),
  };
}

/** Resolve thread parent documents by id, dropping deleted/missing rows. */
export async function getMessagesByIds(messageIds: string[]): Promise<MsgBoardMessage[]> {
  const uniqueIds = Array.from(new Set(messageIds.filter(Boolean)));
  const messages = await Promise.all(
    uniqueIds.map((messageId) => getDocOnce(doc(db, MESSAGES, messageId), mapMsgBoardMessage)),
  );
  return messages.filter(
    (message): message is MsgBoardMessage => message !== null && message.deprecatedOn === null,
  );
}

/** Resolve one message document, used to apply aligned's read-message cutoff. */
export async function getMessageById(messageId: string): Promise<MsgBoardMessage | null> {
  if (!messageId) return null;
  const message = await getDocOnce(doc(db, MESSAGES, messageId), mapMsgBoardMessage);
  return message && message.deprecatedOn === null ? message : null;
}

const UNREAD_PER_CHANNEL_CAP = 100;
const PARTICIPATING_THREAD_QUERY_LIMIT = 200;

/**
 * Fetch the recent candidate messages used by aligned's unread-count query.
 * The author filter is deliberately applied after the query so this keeps the
 * same Firestore query shape as the source client and backend notification
 * code.
 */
export function getUnreadCandidateMessages(
  channelId: string,
  cutoffAt?: string | null,
): Promise<MsgBoardMessage[]> {
  const constraints = [
    where('channelId', '==', channelId),
    where('deprecatedOn', '==', null),
    ...(cutoffAt ? [where('createdAt', '>', cutoffAt)] : []),
    orderBy('createdAt', 'desc'),
    fbLimit(UNREAD_PER_CHANNEL_CAP),
  ];
  return getQueryOnce(query(collection(db, MESSAGES), ...constraints), mapMsgBoardMessage);
}

/** Fetch the user's authored messages used to identify participated threads. */
export function getUserMessagesForChannel(
  channelId: string,
  userId: string,
): Promise<MsgBoardMessage[]> {
  const q = query(
    collection(db, MESSAGES),
    where('channelId', '==', channelId),
    where('authorId', '==', userId),
    where('deprecatedOn', '==', null),
    fbLimit(PARTICIPATING_THREAD_QUERY_LIMIT),
  );
  return getQueryOnce(q, mapMsgBoardMessage);
}

/**
 * Count unread messages using aligned's exact rules: ignore the user's own
 * posts, count top-level messages, and count replies only in threads the user
 * has participated in (by posting at the root or replying).
 */
export async function countUnreadMessagesForChannel(params: {
  channelId: string;
  userId: string;
  cutoffAt?: string | null;
}): Promise<number> {
  const candidates = await getUnreadCandidateMessages(params.channelId, params.cutoffAt);
  const unreadCandidates = candidates.filter((message) => message.authorId !== params.userId);
  const hasThreadReplies = unreadCandidates.some((message) => !!message.parentMessageId);
  if (!hasThreadReplies) return unreadCandidates.length;

  const authoredMessages = await getUserMessagesForChannel(params.channelId, params.userId);
  const participatingThreadIds = new Set<string>();
  for (const message of authoredMessages) {
    participatingThreadIds.add(message.parentMessageId || message.id);
  }
  return unreadCandidates.filter(
    (message) => !message.parentMessageId || participatingThreadIds.has(message.parentMessageId),
  ).length;
}

/**
 * Keep the expert's global unread-message total live. Channel activity and
 * read-receipt listeners both trigger the same exact per-channel count used by
 * the message-board page, so the sidebar follows new posts and reads without a
 * page refresh.
 */
export function subscribeToUnreadMessagesForUser(
  userId: string,
  onNext: (count: number) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  let active = true;
  let channels = new Map<string, Channel>();
  let receiptsByChannel = new Map<string, ChannelReadReceipt>();
  let countsByChannel = new Map<string, number>();
  let membershipUnsubscribe: Unsubscribe | null = null;
  let readReceiptsUnsubscribe: Unsubscribe | null = null;
  const channelUnsubscribes = new Map<string, Unsubscribe>();
  const channelRefreshSequences = new Map<string, number>();
  let channelSyncSequence = 0;

  const reportError = (error: unknown): void => {
    if (!active || !onError) return;
    onError(error instanceof Error ? error : new Error(String(error)));
  };

  const emit = (): void => {
    if (!active) return;
    onNext(Array.from(countsByChannel.values()).reduce((sum, count) => sum + count, 0));
  };

  const refreshChannel = async (channelId: string): Promise<void> => {
    const refreshSequence = (channelRefreshSequences.get(channelId) ?? 0) + 1;
    channelRefreshSequences.set(channelId, refreshSequence);
    const receipt = receiptsByChannel.get(channelId);
    let lastReadMessageAt: string | null = null;

    if (receipt?.lastReadMessageId) {
      try {
        lastReadMessageAt = (await getMessageById(receipt.lastReadMessageId))?.createdAt ?? null;
      } catch {
        // Fall back to lastReadAt when the receipt's message is unavailable.
      }
    }

    const cutoffAt = resolveMessageBoardReadCutoff({
      lastReadAt: receipt?.lastReadAt,
      lastReadMessageAt,
    });
    let unreadCount = 0;
    try {
      unreadCount = await countUnreadMessagesForChannel({ channelId, userId, cutoffAt });
    } catch (error) {
      reportError(error);
      return;
    }

    if (
      !active ||
      channelRefreshSequences.get(channelId) !== refreshSequence ||
      !channels.has(channelId)
    ) {
      return;
    }
    countsByChannel.set(channelId, unreadCount);
    emit();
  };

  const refreshAllChannels = (): void => {
    void Promise.all(Array.from(channels.keys()).map((channelId) => refreshChannel(channelId)));
  };

  const syncChannels = async (memberships: ChannelMembership[]): Promise<void> => {
    const syncSequence = ++channelSyncSequence;
    const channelIds = Array.from(new Set(memberships.map((membership) => membership.channelId).filter(Boolean)));
    const nextChannels = await getChannelsByIds(channelIds);
    if (!active || syncSequence !== channelSyncSequence) return;

    const nextById = new Map(nextChannels.map((channel) => [channel.id, channel]));
    for (const [channelId, unsubscribe] of channelUnsubscribes) {
      if (!nextById.has(channelId)) {
        unsubscribe();
        channelUnsubscribes.delete(channelId);
        countsByChannel.delete(channelId);
        channelRefreshSequences.delete(channelId);
      }
    }
    channels = nextById;

    const subscribeToChannelActivity = (channelId: string): void => {
      if (channelUnsubscribes.has(channelId)) return;
      const unsubscribe = subscribeToChannel(
        channelId,
        (updatedChannel) => {
          if (!active) return;
          if (!updatedChannel || updatedChannel.deprecatedOn !== null) {
            channels.delete(channelId);
            countsByChannel.delete(channelId);
            channelUnsubscribes.get(channelId)?.();
            channelUnsubscribes.delete(channelId);
            emit();
            return;
          }
          channels.set(channelId, updatedChannel);
          void refreshChannel(channelId);
        },
        reportError,
      );
      channelUnsubscribes.set(channelId, unsubscribe);
    };

    nextChannels.forEach((channel) => subscribeToChannelActivity(channel.id));

    await Promise.all(nextChannels.map((channel) => refreshChannel(channel.id)));
    emit();
  };

  const handleMemberships = (memberships: ChannelMembership[]): void => {
    void syncChannels(memberships).catch(reportError);
  };

  const initialize = async (): Promise<void> => {
    try {
      const [memberships, receipts] = await Promise.all([
        getUserMemberships(userId),
        getUserReadReceipts(userId).catch(() => []),
      ]);
      if (!active) return;
      receiptsByChannel = latestReadReceiptsByChannel(receipts);
      await syncChannels(memberships);
      if (!active) return;

      readReceiptsUnsubscribe = subscribeToUserReadReceipts(
        userId,
        (nextReceipts) => {
          receiptsByChannel = latestReadReceiptsByChannel(nextReceipts);
          refreshAllChannels();
        },
        reportError,
      );
      membershipUnsubscribe = subscribeToUserMemberships(userId, handleMemberships, reportError);
    } catch (error) {
      reportError(error);
    }
  };

  void initialize();

  return () => {
    active = false;
    membershipUnsubscribe?.();
    readReceiptsUnsubscribe?.();
    channelUnsubscribes.forEach((unsubscribe) => unsubscribe());
    channelUnsubscribes.clear();
  };
}

/**
 * Real-time subscription to the newest 50 non-deprecated messages of a
 * channel. Matches aligned's `startListeningToChannel` query shape (50-limit,
 * order-by createdAt desc). Callers own the unsubscribe.
 *
 * Note: this query is scoped to *top-level* messages when combined with the
 * thread panel (which subscribes on `parentMessageId` separately). Threaded
 * replies still appear in this stream too — the UI filters them out.
 */
export function subscribeToChannelMessages(
  channelId: string,
  onNext: (messages: MsgBoardMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, MESSAGES),
    where('channelId', '==', channelId),
    where('deprecatedOn', '==', null),
    orderBy('createdAt', 'desc'),
    fbLimit(MESSAGES_LIVE_LIMIT),
  );
  return subscribeToQuery(q, mapMsgBoardMessage, onNext, {
    context: `subscribeToChannelMessages(${channelId})`,
    onError,
  });
}

/**
 * One-shot fetch of thread replies, ordered by `createdAt ASC` (matches
 * aligned's `getThreadReplies`). Reactions are NOT hydrated here — the
 * viewmodel batches that separately via `getReactionsForMessages`.
 */
export function getThreadReplies(
  channelId: string,
  parentMessageId: string,
): Promise<MsgBoardMessage[]> {
  const q = query(
    collection(db, MESSAGES),
    where('channelId', '==', channelId),
    where('parentMessageId', '==', parentMessageId),
    where('deprecatedOn', '==', null),
    orderBy('createdAt', 'asc'),
  );
  return getQueryOnce(q, mapMsgBoardMessage);
}

/**
 * Real-time subscription to a thread — same filter as `getThreadReplies` but
 * live. Used by the thread panel so a reply landing from another client
 * shows up immediately.
 */
export function subscribeToThreadReplies(
  channelId: string,
  parentMessageId: string,
  onNext: (replies: MsgBoardMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, MESSAGES),
    where('channelId', '==', channelId),
    where('parentMessageId', '==', parentMessageId),
    where('deprecatedOn', '==', null),
    orderBy('createdAt', 'asc'),
  );
  return subscribeToQuery(q, mapMsgBoardMessage, onNext, {
    context: `subscribeToThreadReplies(${channelId}/${parentMessageId})`,
    onError,
  });
}

/**
 * Real-time subscription to a channel doc — used to react to `lastMessageAt`
 * bumps so the sidebar can reorder / show a "new" indicator. Matches
 * aligned's `_channelActivitySubscriptions` per-channel snapshot listener.
 */
export function subscribeToChannel(
  channelId: string,
  onNext: (channel: Channel | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return subscribeToDoc(doc(db, CHANNELS, channelId), mapChannel, onNext, {
    context: `subscribeToChannel(${channelId})`,
    onError,
  });
}

// ---------------------------------------------------------------------------
// markChannelAsRead — the one write in this slice.
// ---------------------------------------------------------------------------

export interface MarkChannelAsReadOptions {
  channelId: string;
  userId: string;
  firebaseId: string;
  /** The requested message id; aligned prefers the newest loaded message. */
  lastReadMessageId?: string | null;
  /** Newest-first message cache used to resolve the exact aligned read id. */
  cachedMessages?: MsgBoardMessage[];
  /** ISO now; passed in so the write is deterministic (tests). Defaults to `Date.now()`. */
  nowIso?: string;
}

/**
 * Idempotent read-receipt write, ported from aligned's
 * `createOrUpdateReadReceiptWithTransaction`. One canonical doc per (userId,
 * channelId) — doc id is `rr_<base64url(userId\nchannelId)>` (padding
 * stripped). The caller-level channel/message guard avoids duplicate writes;
 * otherwise this merge-writes the receipt fields, preserving whatever
 * notification-tracking fields the prior receipt already had (a read-receipt
 * write must never advance those itself — only the server-side notification
 * sender does; see `buildReadReceiptDoc`'s doc comment for the bug this fixes).
 * On success it reads the canonical receipt back, matching aligned's
 * local-cache refresh.
 *
 */
export async function markChannelAsRead(opts: MarkChannelAsReadOptions): Promise<ChannelReadReceipt | null> {
  const { channelId, userId, firebaseId } = opts;
  const lastReadMessageId = resolveMessageBoardReadMessageId({
    requestedMessageId: opts.lastReadMessageId,
    cachedMessages: opts.cachedMessages,
  });
  if (!channelId || !userId || !firebaseId || !lastReadMessageId) return null;

  const canonicalId = canonicalReadReceiptId(userId, channelId);
  const sessionKey = channelId;
  if (lastMarkedReadMessageIds.get(sessionKey) === lastReadMessageId) return null;
  lastMarkedReadMessageIds.set(sessionKey, lastReadMessageId);

  const requestedNowIso = opts.nowIso;
  const receiptsQuery = query(
    collection(db, CHANNEL_READ_RECEIPTS),
    where('userId', '==', userId),
    where('channelId', '==', channelId),
    where('deprecatedOn', '==', null),
  );

  try {
    // Match aligned's web-safe workaround: query active receipts outside the
    // transaction, then use the transaction only for the migration + write.
    const existingSnapshot = await getDocs(receiptsQuery);
    const existingReceipts = existingSnapshot.docs.map((receipt) => ({
      id: receipt.id,
      data: receipt.data() as Record<string, unknown>,
    }));
    const canonicalReceipt = existingReceipts.find((receipt) => receipt.id === canonicalId);
    const legacyActiveReceipts = existingReceipts.filter((receipt) => receipt.id !== canonicalId);

    await runTransaction(async (transaction) => {
      // Aligned timestamps inside the transaction callback, so a retried
      // transaction receives a fresh production timestamp. Tests may still
      // inject a stable value through `nowIso`.
      const nowIso = requestedNowIso ?? new Date().toISOString();
      const sourceReceiptData =
        canonicalReceipt?.data ?? legacyActiveReceipts[0]?.data ?? null;

      for (const legacyReceipt of legacyActiveReceipts) {
        transaction.update(doc(db, CHANNEL_READ_RECEIPTS, legacyReceipt.id), {
          deprecatedOn: nowIso,
          deprecatedReason: 'canonical_read_receipt_write',
          supersededByReceiptId: canonicalId,
        });
      }

      const payload = buildReadReceiptDoc({
        canonicalId,
        channelId,
        userId,
        firebaseId,
        lastReadMessageId,
        nowIso,
        preserveFrom: sourceReceiptData as ReadReceiptPreservedFields | null,
      });
      transaction.set(doc(db, CHANNEL_READ_RECEIPTS, canonicalId), payload, { merge: true });
    });
    const updatedReceipt = await getDocOnce(
      doc(db, CHANNEL_READ_RECEIPTS, canonicalId),
      mapChannelReadReceipt,
    );
    // Firestore normally returns the document immediately after the
    // transaction. Keep aligned's replication-lag fallback so the caller can
    // still update its local unread state if that read briefly misses.
    const fallbackNowIso = requestedNowIso ?? new Date().toISOString();
    return updatedReceipt ?? {
      id: canonicalId,
      channelId,
      userId,
      firebaseId,
      lastReadAt: fallbackNowIso,
      createdAt: fallbackNowIso,
      deprecatedOn: null,
      lastReadMessageId,
      lastEmailedForMessageId: null,
    };
  } catch (err) {
    // A failed write must be retryable on the next snapshot, matching
    // aligned's removal from `_lastMarkedReadMessageIds` in its catch path.
    if (lastMarkedReadMessageIds.get(sessionKey) === lastReadMessageId) {
      lastMarkedReadMessageIds.delete(sessionKey);
    }
    throw err;
  }
}

// Unread-count derivation lives in `utils/messageBoardUnread.ts` so it stays
// pure (no firebase import) and can be unit-tested without a runtime.

// ---------------------------------------------------------------------------
// Membership lookup (single user + channel), used by the composer gate.
// ---------------------------------------------------------------------------

export async function getUserChannelMembership(
  channelId: string,
  userId: string,
): Promise<ChannelMembership | null> {
  const rows = await getQueryOnce(
    query(
      collection(db, CHANNEL_MEMBERSHIPS),
      where('channelId', '==', channelId),
      where('userId', '==', userId),
      where('deprecatedOn', '==', null),
      fbLimit(1),
    ),
    mapChannelMembership,
  );
  return rows[0] ?? null;
}

// ---------------------------------------------------------------------------
// sendMessage — the second write in this feature. Matches aligned's `sendMessage`:
//   1. write the message, its `id` field already holding its own doc id
//      (aligned addDocs then updates the id back; `createDoc` does it in one
//      write so a failure can't leave the message with a blank id)
//   2. bump `channels/{channelId}.lastMessageAt`
//   3. if reply, bump the parent's `replyCount` via FieldValue.increment(1)
// Fire-and-forget notification emails + unread-count recompute are
// **deferred** — they belong with the notification sub-slice, not the write.
// ---------------------------------------------------------------------------

export interface SendMessageOptions {
  channelId: string;
  authorId: string;
  firebaseId: string;
  authorEmail: string;
  /** Aligned uses `messageBoardUserName ?? 'unknown'`. The caller resolves that fallback. */
  authorUserName: string;
  content: string;
  parentMessageId?: string | null;
  attachmentUrls?: string[];
  /** ISO now; passed in for deterministic tests. Defaults to `Date.now()`. */
  nowIso?: string;
}

export async function sendMessage(opts: SendMessageOptions): Promise<MsgBoardMessage> {
  const nowIso = opts.nowIso ?? new Date().toISOString();
  const channelSnapshot = await getDoc(doc(db, CHANNELS, opts.channelId));
  if (!channelSnapshot.exists() || channelSnapshot.data().deprecatedOn != null) {
    throw new Error('This conversation is no longer available.');
  }
  if (channelSnapshot.data().type === 'direct_message') {
    const activeMembership = await getDocs(query(
      collection(db, CHANNEL_MEMBERSHIPS),
      where('channelId', '==', opts.channelId),
      where('userId', '==', opts.authorId),
      where('deprecatedOn', '==', null),
      fbLimit(1),
    ));
    if (activeMembership.empty) throw new Error('This conversation is no longer available.');
  }
  const parentMessageId = opts.parentMessageId ?? null;
  const payload = buildNewMessageDoc({
    channelId: opts.channelId,
    authorId: opts.authorId,
    firebaseId: opts.firebaseId,
    authorEmail: opts.authorEmail,
    authorUserName: opts.authorUserName,
    content: opts.content,
    parentMessageId,
    attachmentUrls: opts.attachmentUrls ?? [],
    nowIso,
  });

  const created = await createDoc(MESSAGES, payload);
  const messageId = created.id;
  await updateDoc(doc(db, CHANNELS, opts.channelId), { lastMessageAt: nowIso });
  if (parentMessageId) {
    await updateDoc(doc(db, MESSAGES, parentMessageId), { replyCount: increment(1) });
  }

  return mapMsgBoardMessage(messageId, created.data);
}

function attachmentMediaType(fileName: string): MessageBoardAttachment['mediaType'] {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
  if (['mp4', 'webm', 'mov'].includes(extension)) return 'video';
  return 'document';
}

function newAttachmentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `media-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Upload and register a message-board attachment exactly like aligned's
 * `uploadFileAndSendMessage`: Storage under `media/`, then a `media_items`
 * record tagged `message_board`. The caller stages the returned URL until the
 * user sends the message.
 */
export async function uploadMessageBoardAttachment(
  file: File,
  userId: string,
): Promise<MessageBoardAttachment> {
  // Names the Storage object only — the `media_items` document's `id` field is
  // its own Firestore document id, never this one.
  const storageKey = newAttachmentId();
  const fileName = file.name || 'attachment';
  const safeFileName = fileName.replace(/[^A-Za-z0-9._-]/g, '_') || 'attachment';
  const mediaType = attachmentMediaType(fileName);
  const url = await uploadFile(`media/${storageKey}_${safeFileName}`, file, {
    contentType: file.type || undefined,
    cacheControl: 'no-cache, no-store, must-revalidate',
  });
  if (!url) throw new Error('The attachment could not be uploaded.');

  await createDoc('media_items', {
    url,
    fileName,
    mediaType,
    uploadedAt: new Date().toISOString(),
    uploadedBy: userId,
    tags: ['message_board'],
    thumbnailUrl: mediaType === 'image' ? url : null,
  });

  return { url, fileName, mediaType, isImage: mediaType === 'image' };
}

// ---------------------------------------------------------------------------
// Reactions — batched hydration + toggle. Aligned hydrates one message at a
// time; we batch via Firestore's `in` (max 30 ids per query) to cut round trips
// while keeping the same *observable* behavior (the reactions delivered per
// message are the same set of docs).
// ---------------------------------------------------------------------------

const IN_CLAUSE_MAX = 30;

/**
 * Non-deprecated reactions for the given messages, grouped by messageId. Empty
 * `messageIds` returns an empty map without a round trip.
 */
export async function getReactionsForMessages(
  messageIds: string[],
): Promise<Map<string, Reaction[]>> {
  const grouped = new Map<string, Reaction[]>();
  if (messageIds.length === 0) return grouped;
  const uniqueMessageIds = Array.from(new Set(messageIds.filter(Boolean)));
  for (let i = 0; i < uniqueMessageIds.length; i += IN_CLAUSE_MAX) {
    const batch = uniqueMessageIds.slice(i, i + IN_CLAUSE_MAX);
    const rows = await getQueryOnce(
      query(
        collection(db, REACTIONS),
        where('messageId', 'in', batch),
        where('deprecatedOn', '==', null),
      ),
      mapReaction,
    );
    for (const r of rows) {
      const bucket = grouped.get(r.messageId) ?? [];
      bucket.push(r);
      grouped.set(r.messageId, bucket);
    }
  }
  return grouped;
}

/** The active reaction (if any) for a specific (message, emoji, user) triple. */
async function findExistingReaction(
  messageId: string,
  emoji: string,
  authorId: string,
): Promise<Reaction | null> {
  const rows = await getQueryOnce(
    query(
      collection(db, REACTIONS),
      where('messageId', '==', messageId),
      where('emoji', '==', emoji),
      where('authorId', '==', authorId),
      where('deprecatedOn', '==', null),
      fbLimit(1),
    ),
    mapReaction,
  );
  return rows[0] ?? null;
}

export interface ToggleReactionOptions {
  messageId: string;
  emoji: string;
  authorId: string;
  firebaseId: string;
  authorEmail: string;
  /** ISO now; passed in for deterministic tests. Defaults to `Date.now()`. */
  nowIso?: string;
}

/**
 * Toggle the current user's reaction of `emoji` on `messageId`:
 *  - If one exists, soft-delete it (`deprecatedOn = nowIso`).
 *  - Otherwise, create a fresh reaction doc.
 * Mirrors aligned's `toggleReaction` — no transaction (aligned doesn't use one
 * either), so a rapid double-click can create two docs. Aligned tolerates
 * that; we match. Returns the resulting state: `'added'` or `'removed'`.
 */
export async function toggleReaction(opts: ToggleReactionOptions): Promise<'added' | 'removed'> {
  const nowIso = opts.nowIso ?? new Date().toISOString();
  const existing = await findExistingReaction(opts.messageId, opts.emoji, opts.authorId);
  if (existing) {
    await updateDoc(doc(db, REACTIONS, existing.id), { deprecatedOn: nowIso });
    return 'removed';
  }
  const payload = buildNewReactionDoc({
    messageId: opts.messageId,
    emoji: opts.emoji,
    authorId: opts.authorId,
    firebaseId: opts.firebaseId,
    authorEmail: opts.authorEmail,
    nowIso,
  });
  await createDoc(REACTIONS, payload);
  return 'added';
}

// ---------------------------------------------------------------------------
// editMessage / deleteMessage — author-gated. `editMessage` sets `content` +
// `updatedAt`; `deleteMessage` is a soft delete (sets `deprecatedOn`). Both
// return `false` if the caller isn't the author, matching aligned's behavior
// (aligned logs an error and returns; we match).
// ---------------------------------------------------------------------------

export async function editMessage(params: {
  messageId: string;
  userId: string;
  newContent: string;
  nowIso?: string;
}): Promise<boolean> {
  const snap = await getDoc(doc(db, MESSAGES, params.messageId));
  if (!snap.exists()) return false;
  const data = snap.data() as Record<string, unknown>;
  if (data.authorId !== params.userId) return false;
  const nowIso = params.nowIso ?? new Date().toISOString();
  await updateDoc(doc(db, MESSAGES, params.messageId), {
    content: params.newContent,
    updatedAt: nowIso,
  });
  return true;
}

/** Update both delivery-specific channel preferences on the live membership. */
export async function updateChannelNotificationPreferences(params: {
  channelId: string;
  userId: string;
  emailPreference: NotificationPreference;
  pushPreference: NotificationPreference;
}): Promise<boolean> {
  const membership = await getUserChannelMembership(params.channelId, params.userId);
  if (!membership) return false;
  await updateDoc(doc(db, CHANNEL_MEMBERSHIPS, membership.id), {
    emailNotificationPreference: params.emailPreference,
    pushNotificationPreference: params.pushPreference,
  });
  return true;
}

export async function deleteMessage(params: {
  messageId: string;
  userId: string;
  nowIso?: string;
}): Promise<boolean> {
  const snap = await getDoc(doc(db, MESSAGES, params.messageId));
  if (!snap.exists()) return false;
  const data = snap.data() as Record<string, unknown>;
  if (data.authorId !== params.userId) return false;
  const nowIso = params.nowIso ?? new Date().toISOString();
  await updateDoc(doc(db, MESSAGES, params.messageId), { deprecatedOn: nowIso });
  return true;
}

// ---------------------------------------------------------------------------
// Channel creation — ported from `createChannel`/`_createChannelMembership`/
// `addMemberToChannel`/`updateMemberModeratorStatus` (used by
// `manager_create_job`'s optional "Create message board" step).
// ---------------------------------------------------------------------------

export interface ChannelMemberRef {
  id: string;
  firebaseId: string;
  email: string;
  messageBoardUserName?: string;
  firstName?: string;
  firstNamePreferred?: string;
  canSeeMessageBoard?: boolean;
}

/**
 * Ported from `_createChannelMembership`. Aligned wraps this in a Firestore
 * transaction (query-for-existing then transactional set) to guard against a
 * race between concurrent callers adding the same user to the same channel;
 * this port simplifies to a plain query-then-set (no transaction) since every
 * caller of this function runs sequentially (channel creation invites its
 * owner, then each admin, one at a time) — the race window aligned guards
 * against doesn't arise in this port's call sites.
 */
async function createChannelMembership(
  channelId: string,
  user: ChannelMemberRef,
  role: ChannelMembership['role'],
  isModerator = false,
  notificationPreference: NotificationPreference = DEFAULT_CHANNEL_NOTIFICATION_PREFERENCE,
): Promise<{ membership: ChannelMembership; isNew: boolean }> {
  const existing = await getQueryOnce(
    query(
      collection(db, CHANNEL_MEMBERSHIPS),
      where('channelId', '==', channelId),
      where('userId', '==', user.id),
      where('deprecatedOn', '==', null),
      fbLimit(1),
    ),
    mapChannelMembership,
  );
  if (existing.length > 0) return { membership: existing[0], isNew: false };

  const membershipRef = doc(collection(db, CHANNEL_MEMBERSHIPS));
  const data = {
    id: membershipRef.id,
    channelId,
    userId: user.id,
    firebaseId: user.firebaseId,
    userEmail: user.email,
    userName: user.messageBoardUserName ?? 'unknown',
    role,
    pushNotificationPreference: notificationPreference,
    emailNotificationPreference: notificationPreference,
    isModerator,
    createdAt: new Date().toISOString(),
    deprecatedOn: null,
  };
  await setDoc(membershipRef, data);
  return { membership: mapChannelMembership(membershipRef.id, data), isNew: true };
}

export interface CreateChannelInput {
  name: string;
  description: string;
  owner: ChannelMemberRef;
  relatedJobId?: string | null;
  jobInstructions?: string | null;
  type?: Channel['type'];
  initialMembers?: ChannelMemberRef[];
  allowModeratorPosts?: boolean;
  allowMemberPosts?: boolean;
  defaultNotificationPreference?: NotificationPreference;
}

/**
 * Ported from `MessageBoardService.createChannel`: create the channel doc,
 * then an owner membership. Aligned creates the
 * owner membership with `isModerator: false`, then makes a *second* round
 * trip right after (fetch the channel's members, find the one matching the
 * owner, `updateMemberModeratorStatus` it to `true`) — this port folds that
 * into the same membership write instead (`isModerator: true` from the
 * start), same end state, one fewer read+write.
 */
export async function createChannel(input: CreateChannelInput): Promise<Channel> {
  const now = new Date().toISOString();
  const requestedNotificationPreference =
    input.defaultNotificationPreference
    ?? (input.type === 'direct_message' ? 'immediate' : DEFAULT_CHANNEL_NOTIFICATION_PREFERENCE);
  const defaultNotificationPreference = effectiveNotificationPreferenceForChannelName(
    requestedNotificationPreference,
    input.name,
  );
  const data = {
    name: input.name,
    description: input.description,
    ownerId: input.owner.id,
    firebaseId: input.owner.firebaseId,
    type: input.type ?? ('group' as const),
    allowModeratorPosts: input.allowModeratorPosts ?? true,
    allowMemberPosts: input.allowMemberPosts ?? true,
    defaultNotificationPreference,
    createdAt: now,
    updatedAt: null,
    deprecatedOn: null,
    lastMessageAt: null,
    relatedJobId: input.relatedJobId ?? null,
    jobInstructions: input.jobInstructions ?? null,
  };
  const created = await createDoc(CHANNELS, data);

  await createChannelMembership(created.id, input.owner, 'owner', true, defaultNotificationPreference);
  for (const member of input.initialMembers ?? []) {
    await createChannelMembership(created.id, member, 'member', false, defaultNotificationPreference);
  }

  return mapChannel(created.id, created.data);
}

/**
 * The aligned service emits an in-app notification whenever a *new* channel
 * membership is created. Email delivery is best-effort and remains behind the
 * existing HTTP function boundary; the membership itself must not fail if the
 * notification or email endpoint is unavailable.
 */
async function sendChannelMembershipNotification(
  channelId: string,
  user: ChannelMemberRef,
  sendEmail = true,
): Promise<void> {
  try {
    const [channelSnapshot, userSnapshot] = await Promise.all([
      getDoc(doc(db, CHANNELS, channelId)),
      getDoc(doc(db, 'user_accounts', user.id)),
    ]);
    if (!channelSnapshot.exists() || !userSnapshot.exists()) return;

    const channelName = typeof channelSnapshot.data().name === 'string'
      ? channelSnapshot.data().name as string
      : channelId;
    const userData = userSnapshot.data();
    const firebaseId = typeof userData.firebaseId === 'string' ? userData.firebaseId : user.firebaseId;
    const email = typeof userData.email === 'string' ? userData.email : user.email;
    const now = new Date().toISOString();
    await createDoc('notifications', {
      firebaseId,
      userAccountId: user.id,
      message: `You have been added to the channel "${channelName}"`,
      userType: 'EXPERT',
      createdOn: now,
      readOn: null,
      deprecatedOn: null,
      status: 'unread',
    });

    // Local tests and emulator-only runs do not have an HTTP function host.
    // Production keeps the source behavior when the configured function
    // boundary exists, while notification creation remains independently
    // observable and best-effort.
    if (
      sendEmail
      && email
      && !clientEnvironment.useEmulators
      && clientEnvironment.functionsUrl
    ) {
      const privateInfo = await getDoc(doc(db, 'user_accounts', user.id, 'private', 'info'));
      const unsubscribeToken = typeof privateInfo.data()?.unsubscribeToken === 'string'
        ? privateInfo.data()?.unsubscribeToken as string
        : '';
      await callFunction('sendEmailWithTemplate', {
        mail: {
          to: [email],
          template: {
            name: 'channel_membership_notification',
            data: {
              firstName: typeof userData.firstNamePreferred === 'string'
                ? userData.firstNamePreferred
                : typeof userData.firstName === 'string' ? userData.firstName : '',
              channelName,
              unsubscribeToken,
            },
          },
        },
      });
    }
  } catch {
    // Aligned treats notification/email delivery as best-effort.
  }
}

/**
 * Ported from `addMemberToChannel`: enable board access, create an idempotent
 * membership, and notify only when that membership is newly created.
 */
export async function addMemberToChannel(
  channelId: string,
  user: ChannelMemberRef,
  sendEmail = true,
): Promise<void> {
  const [userSnapshot, channelSnapshot] = await Promise.all([
    getDoc(doc(db, 'user_accounts', user.id)),
    getDoc(doc(db, CHANNELS, channelId)),
  ]);
  const canSeeMessageBoard = userSnapshot.exists()
    ? userSnapshot.data().canSeeMessageBoard
    : user.canSeeMessageBoard;
  if (canSeeMessageBoard === false) {
    await updateDoc(doc(db, 'user_accounts', user.id), { canSeeMessageBoard: true });
  }
  const channelData = channelSnapshot.exists() ? channelSnapshot.data() : {};
  const channelDefaultNotificationPreference =
    channelData.defaultNotificationPreference === 'immediate'
    || channelData.defaultNotificationPreference === 'hourly'
    || channelData.defaultNotificationPreference === 'daily'
    || channelData.defaultNotificationPreference === 'never'
      ? channelData.defaultNotificationPreference
      : LEGACY_CHANNEL_NOTIFICATION_PREFERENCE;
  const defaultNotificationPreference = effectiveNotificationPreferenceForChannelName(
    channelDefaultNotificationPreference,
    typeof channelData.name === 'string' ? channelData.name : '',
  );
  const result = await createChannelMembership(
    channelId,
    user,
    'member',
    false,
    defaultNotificationPreference,
  );
  if (result.isNew) await sendChannelMembershipNotification(channelId, user, sendEmail);
}

/** Expert-side counterpart to `addMemberToChannel`: deprecate only the
 * current user's membership and leave channel ownership untouched. */
export async function removeChannelMember(channelId: string, userId: string): Promise<void> {
  const active = await getQueryOnce(
    query(
      collection(db, CHANNEL_MEMBERSHIPS),
      where('channelId', '==', channelId),
      where('userId', '==', userId),
      where('deprecatedOn', '==', null),
      fbLimit(1),
    ),
    mapChannelMembership,
  );
  if (active[0]) {
    await updateDoc(doc(db, CHANNEL_MEMBERSHIPS, active[0].id), { deprecatedOn: new Date().toISOString() });
  }
}

function memberDisplayName(user: ChannelMemberRef): string {
  return user.firstNamePreferred ?? user.firstName ?? user.email ?? user.id;
}

/**
 * Port of aligned's admin-side `createDirectMessageChannel`. Existing DMs are
 * reused; a new one creates both memberships, posts the source-compatible
 * starter message, marks the admin read, and seeds the other user's canonical
 * receipt without changing notification-tracking fields.
 */
export async function createDirectMessageChannel(
  admin: ChannelMemberRef,
  user: ChannelMemberRef,
): Promise<Channel> {
  const existingChannel = await findDirectMessageChannel(admin.id, user.id);
  if (existingChannel) return existingChannel;

  const channel = await createChannel({
    name: `DM: ${memberDisplayName(admin)} & ${memberDisplayName(user)}`,
    description: `Direct messages between ${memberDisplayName(admin)} and ${memberDisplayName(user)}`,
    owner: admin,
    initialMembers: [user],
    type: 'direct_message',
    // Direct messages remain immediate; the hourly default is for group
    // message boards created through the shared channel path.
    defaultNotificationPreference: 'immediate',
  });
  const initialMessage = await sendMessage({
    channelId: channel.id,
    authorId: admin.id,
    firebaseId: admin.firebaseId,
    authorEmail: admin.email,
    authorUserName: admin.messageBoardUserName ?? 'unknown',
    content: 'Started a conversation',
  });

  await markChannelAsRead({
    channelId: channel.id,
    userId: admin.id,
    firebaseId: admin.firebaseId,
    lastReadMessageId: initialMessage.id,
    cachedMessages: [initialMessage],
  });

  const nowIso = new Date().toISOString();
  const userReceiptId = canonicalReadReceiptId(user.id, channel.id);
  await setDoc(doc(db, CHANNEL_READ_RECEIPTS, userReceiptId), {
    id: userReceiptId,
    channelId: channel.id,
    userId: user.id,
    firebaseId: user.firebaseId,
    deprecatedOn: null,
    lastReadAt: nowIso,
    lastReadMessageId: initialMessage.id,
    createdAt: nowIso,
  }, { merge: true });

  return channel;
}

/** Ported from `updateMemberModeratorStatus`. */
export async function updateMemberModeratorStatus(membershipId: string, isModerator: boolean): Promise<void> {
  await updateDoc(doc(db, CHANNEL_MEMBERSHIPS, membershipId), { isModerator });
}
